import {
  useMemo, useRef, useEffect,
} from 'react';
import {
  AlertTriangle, TrendingUp, TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type {
  TemperatureReading,
  TemperatureThresholds,
  TemperatureAlert,
} from '../../types/storage';

interface TemperatureChartProps {
  readonly readings: TemperatureReading[];
  readonly thresholds: TemperatureThresholds;
  readonly alerts: TemperatureAlert[];
  readonly height?: number;
}

type TrendDirection = 'up' | 'down' | 'stable';

interface ChartStats {
  minTemp: number;
  maxTemp: number;
  avgTemp: number;
  trend: TrendDirection;
}

function calculateStats(readings: TemperatureReading[]): ChartStats {
  if (readings.length === 0) {
    return {
      minTemp: 0,
      maxTemp: 0,
      avgTemp: 0,
      trend: 'stable',
    };
  }

  const temps = readings.map((r) => r.value);
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const avg = temps.reduce((a, b) => a + b, 0) / temps.length;

  const recentSlice = temps.slice(-5);
  const olderSlice = temps.slice(0, 5);
  const recentAvg = recentSlice.reduce((a, b) => a + b, 0) / recentSlice.length;
  const olderAvg = olderSlice.reduce((a, b) => a + b, 0) / olderSlice.length;

  const trendThreshold = 0.5;
  const trendDiff = recentAvg - olderAvg;

  const resolveTrend = (): TrendDirection => {
    if (trendDiff > trendThreshold) return 'up';
    if (trendDiff < -trendThreshold) return 'down';
    return 'stable';
  };

  return {
    minTemp: min,
    maxTemp: max,
    avgTemp: avg,
    trend: resolveTrend(),
  };
}

interface ChartPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

function drawChart(
  ctx: CanvasRenderingContext2D,
  readings: TemperatureReading[],
  thresholds: TemperatureThresholds,
  width: number,
  chartHeight: number
) {
  const padding: ChartPadding = {
    top: 20,
    right: 20,
    bottom: 30,
    left: 50,
  };
  const chartWidth = width - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  ctx.clearRect(0, 0, width, chartHeight);

  const allTemps = [
    ...readings.map((r) => r.value),
    thresholds.minCritical,
    thresholds.maxCritical,
  ];
  const dataMin = Math.min(...allTemps) - 2;
  const dataMax = Math.max(...allTemps) + 2;

  const scaleY = (temp: number) =>
    padding.top + innerHeight - ((temp - dataMin) / (dataMax - dataMin)) * innerHeight;

  const scaleX = (index: number) =>
    padding.left + (index / (readings.length - 1)) * chartWidth;

  const drawThresholdZone = (minY: number, maxY: number, color: string) => {
    ctx.fillStyle = color;
    ctx.fillRect(
      padding.left,
      Math.min(scaleY(maxY), scaleY(minY)),
      chartWidth,
      Math.abs(scaleY(maxY) - scaleY(minY))
    );
  };

  drawThresholdZone(thresholds.minCritical, dataMin, 'rgba(239, 68, 68, 0.1)');
  drawThresholdZone(thresholds.minWarning, thresholds.minCritical, 'rgba(245, 158, 11, 0.1)');
  drawThresholdZone(thresholds.maxWarning, thresholds.minWarning, 'rgba(34, 197, 94, 0.1)');
  drawThresholdZone(thresholds.maxCritical, thresholds.maxWarning, 'rgba(245, 158, 11, 0.1)');
  drawThresholdZone(dataMax, thresholds.maxCritical, 'rgba(239, 68, 68, 0.1)');

  const drawThresholdLine = (temp: number, color: string, dashed = false) => {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash(dashed ? [4, 4] : []);
    ctx.moveTo(padding.left, scaleY(temp));
    ctx.lineTo(padding.left + chartWidth, scaleY(temp));
    ctx.stroke();
  };

  drawThresholdLine(thresholds.minCritical, 'rgba(239, 68, 68, 0.5)', true);
  drawThresholdLine(thresholds.minWarning, 'rgba(245, 158, 11, 0.5)', true);
  drawThresholdLine(thresholds.maxWarning, 'rgba(245, 158, 11, 0.5)', true);
  drawThresholdLine(thresholds.maxCritical, 'rgba(239, 68, 68, 0.5)', true);

  ctx.beginPath();
  ctx.strokeStyle = '#3b82f6';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);
  readings.forEach((reading, i) => {
    const x = scaleX(i);
    const y = scaleY(reading.value);
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  ctx.stroke();

  readings.forEach((reading, i) => {
    const x = scaleX(i);
    const y = scaleY(reading.value);
    const isAlert =
      reading.value <= thresholds.minCritical ||
      reading.value >= thresholds.maxCritical;
    const isWarning =
      reading.value <= thresholds.minWarning ||
      reading.value >= thresholds.maxWarning;

    const resolveStyle = () => {
      if (isAlert) return {
        radius: 5,
        color: '#ef4444' 
      };
      if (isWarning) return {
        radius: 4,
        color: '#f59e0b' 
      };
      return {
        radius: 3,
        color: '#3b82f6' 
      };
    };
    const style = resolveStyle();

    ctx.beginPath();
    ctx.arc(x, y, style.radius, 0, Math.PI * 2);
    ctx.fillStyle = style.color;
    ctx.fill();
  });

  ctx.fillStyle = '#6b7280';
  ctx.font = '10px sans-serif';
  ctx.textAlign = 'right';

  const yTicks = 5;
  Array.from({ length: yTicks + 1 }, (_, i) => {
    const temp = dataMin + ((dataMax - dataMin) * i) / yTicks;
    const y = scaleY(temp);
    ctx.fillText(`${temp.toFixed(0)}°`, padding.left - 5, y + 3);
    return null;
  });

  ctx.textAlign = 'center';
  const xTicks = Math.min(6, readings.length);
  Array.from({ length: xTicks }, (_, i) => {
    const index = Math.floor((i * (readings.length - 1)) / (xTicks - 1));
    const x = scaleX(index);
    const time = new Date(readings[index].timestamp);
    ctx.fillText(
      time.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      x,
      chartHeight - 10
    );
    return null;
  });
}

export function TemperatureChart({
  readings,
  thresholds,
  alerts,
  height = 200,
}: TemperatureChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const stats = useMemo(() => calculateStats(readings), [readings]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || readings.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    drawChart(ctx, readings, thresholds, rect.width, rect.height);
  }, [readings, thresholds]);

  if (readings.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50"
        style={{ height }}
      >
        <p className="text-gray-500">No temperature data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="text-gray-500">Min: </span>
            <span className="font-medium">{stats.minTemp.toFixed(1)}°</span>
          </div>
          <div>
            <span className="text-gray-500">Max: </span>
            <span className="font-medium">{stats.maxTemp.toFixed(1)}°</span>
          </div>
          <div>
            <span className="text-gray-500">Avg: </span>
            <span className="font-medium">{stats.avgTemp.toFixed(1)}°</span>
          </div>
        </div>
        <TrendIndicator trend={stats.trend} />
      </div>

      <canvas
        ref={canvasRef}
        className="w-full"
        style={{ height }}
      />

      {alerts.length > 0 && <AlertsList alerts={alerts} />}

      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-full bg-green-200" />
          <span>Normal</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-full bg-amber-200" />
          <span>Warning</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-full bg-red-200" />
          <span>Critical</span>
        </div>
      </div>
    </div>
  );
}

function TrendIndicator({ trend }: { readonly trend: TrendDirection }) {
  if (trend === 'up') {
    return (
      <div className="flex items-center gap-1 text-sm">
        <TrendingUp className="h-4 w-4 text-red-500" />
        <span className="text-red-600">Rising</span>
      </div>
    );
  }

  if (trend === 'down') {
    return (
      <div className="flex items-center gap-1 text-sm">
        <TrendingDown className="h-4 w-4 text-blue-500" />
        <span className="text-blue-600">Falling</span>
      </div>
    );
  }

  return <span className="text-sm text-gray-500">Stable</span>;
}

function AlertsList({ alerts }: { readonly alerts: TemperatureAlert[] }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">Recent Alerts</h4>
      <div className="max-h-32 space-y-1 overflow-y-auto">
        {alerts.slice(0, 5).map((alert) => (
          <div
            key={alert.id}
            className={cn(
              'flex items-center justify-between rounded-md px-3 py-2 text-sm',
              alert.type === 'critical'
                ? 'bg-red-50 text-red-800'
                : 'bg-amber-50 text-amber-800',
              alert.acknowledged && 'opacity-60'
            )}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span>{alert.message}</span>
            </div>
            <span className="text-xs">
              {new Date(alert.timestamp).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
